import { useRouter } from 'next/router';
import React, { useContext, useRef } from 'react';
import { AFTER_APP } from 'Components/frameworks/portal/Portal.constants';
import { COLORS } from 'Components/utils/color/colors';
import { ID_LAYER_HEADER__CA__ACTIVITIES } from 'Components/utils/identifiers/constants';
import { LAYER_NAME_UPSELL_ACTIVITIES } from 'Components/utils/layers/layers.constants';
import { camelToSnakeCase } from 'Components/utils/string/string.util';
import { TRACK_PAGE_NAMES } from 'Tracking/constants';
import { ServicesContext } from 'Context/ServicesContext';
import { t } from 'Src/i18n';
import { Title } from 'Components/frameworks/title/Title';
import { Toast } from 'Components/frameworks/toast/Toast';
import { ClosableLayer } from 'Components/widgets/layer/ClosableLayer';
import { BookingEngineTrackingContext } from 'Views/booking/BookingEngineTrackingContext';
import { LayerActivitiesBody } from './LayerActivitiesBody';
type Props = PropsWithPageTracking<{
  categories: Array<ActivitiesCategoryEntity>;
  thematics: Array<ActivitiesThematicEntity>;
  forwardRef?: ReactRef;
  layerName?: string;
}>;
export const LayerActivities = ({
  categories,
  forwardRef,
  layerName,
  thematics,
  tracking,
}: Props) => {
  const { updateInfo, setUpdateInfo } = useContext(ServicesContext);
  const beEngineTrackingContext = useContext(BookingEngineTrackingContext);
  const toasterRef = useRef();
  const router = useRouter();
  const { query: queryParams } = router;
  const items = queryParams.type === 'CATEGORIES' ? categories : thematics;
  const category = items.find((c) => c.id === queryParams?.catId);

  if (!category) {
    return null;
  }

  const stateTracking = tracking || {
    pageName: TRACK_PAGE_NAMES.BE_LAYER_ACTIVITIES,
    payload: {
      ...beEngineTrackingContext,
      activity_type: camelToSnakeCase(category.upsellType),
      availability: category.onlineBookableCount !== 0 ? 'yes' : 'no',
    },
  };
  const title = category.name || t('booking.activities.otherActivities');
  return (
    <ClosableLayer
      ariaLabel={title}
      ariaLabelledby={ID_LAYER_HEADER__CA__ACTIVITIES}
      bgColor={COLORS.WHITE}
      dataTestId="UpsellActivitiesLayer"
      externalFocusTrapRefs={[forwardRef, toasterRef]}
      hasAnimateEnter
      header={
        <Title
          className="w-full justify-center"
          id={ID_LAYER_HEADER__CA__ACTIVITIES}
          theme="title-brand-n3"
        >
          {title}
        </Title>
      }
      name={layerName || LAYER_NAME_UPSELL_ACTIVITIES}
      params={['catId', 'type']}
      tracking={stateTracking}
    >
      <Toast
        color={COLORS.SIENNA}
        onClose={() => setUpdateInfo && setUpdateInfo(null)}
        isFixed
        portalName={AFTER_APP}
        ref={toasterRef}
        visible={!!updateInfo}
      >
        <span className="flex items-center gap-10">
          {!!updateInfo?.itemsNumber && (
            <span className="inline-flex aspect-square h-30 shrink-0 items-center justify-center rounded-full bg-success p-5 text-white">
              +{updateInfo.itemsNumber}
            </span>
          )}
          {updateInfo?.message}
        </span>
      </Toast>
      <LayerActivitiesBody category={category} />
    </ClosableLayer>
  );
};
